import { useTranslation } from 'react-i18next';
import {
  useDeleteTrainingActivityType,
  useGetTrainingActivityTypes,
} from '../../../services/redux/api/trainingActivityTypesApi';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { ITrainingActivityType } from '../../../types/interfaces/training-activity-type';

export function useTrainingActivityTypesComponent(classes) {
  const { t } = useTranslation();
  const { data: trainingActivityTypes } = useGetTrainingActivityTypes();
  const [itemForDelete, setItemForDelete] =
    useState<ITrainingActivityType>(null);
  const [itemForEditing, setItemForEditing] =
    useState<ITrainingActivityType>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteTrainingActivityType] = useDeleteTrainingActivityType();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);

  const filteredValues = useMemo(
    () =>
      trainingActivityTypes?.filter((item: ITrainingActivityType) => {
        const filterValue = filter.toLowerCase();
        return (
          String(item.name ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(item.nameKey ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(item.met ?? '')
            .toLowerCase()
            .includes(filterValue)
        );
      }),
    [trainingActivityTypes, filter],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.trainingActivityTypes?.canEdit ?? false,
      canDelete: access?.trainingActivityTypes?.canDelete ?? false,
    },
    (item: ITrainingActivityType) => {
      setItemForEditing(item);
      setShowEditModal(true);
    },
    (item: ITrainingActivityType) => {
      setItemForDelete(item);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.TRAINING_ACTIVITY_TYPE_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteTrainingActivityType(itemForDelete.id);
  }

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
  };
}
